import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ProductCard, { explodeColourCards } from "../components/storefront/ProductCard";
import SeoMeta from "../components/storefront/SeoMeta";
import { api, mediaUrl } from "../lib/api";
import { pickLocale } from "../lib/format";
import { useI18n } from "../lib/i18n";

export default function TagPage() {
  const { slug } = useParams();
  const { locale, t } = useI18n();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 24;

  useEffect(() => {
    setPage(1);
  }, [slug]);

  useEffect(() => {
    setData(null);
    setMissing(false);
    api.get(`/tags/${slug}`, { params: { page, page_size: pageSize } })
      .then((response) => setData(response.data))
      .catch(() => setMissing(true));
  }, [page, slug]);

  if (missing) {
    return (
      <div className="min-h-screen bg-[var(--adf-cream)] text-[var(--adf-ink)]">
        <Header />
        <main id="main" className="adf-container py-24 text-center">
          <h1 className="adf-heading text-5xl">{t("tags.not_found", "Tag tapılmadı")}</h1>
          <Link to="/shop" className="adf-cta adf-cta-outline mt-8 inline-block">{t("shop.title", "Shop")}</Link>
        </main>
        <Footer />
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[var(--adf-cream)]"><Header /></div>;

  const tag = data.tag;
  const products = data.items ?? [];
  const articles = data.articles ?? [];
  const productTotal = data.total ?? products.length;
  const pageCount = Math.max(1, Math.ceil(productTotal / pageSize));
  const title = pickLocale(tag.seo_title, locale) || tag.name;
  const description = pickLocale(tag.meta_description, locale);

  return (
    <div className="min-h-screen bg-[var(--adf-cream)] text-[var(--adf-ink)]" data-testid="tag-page">
      <SeoMeta
        title={tag.seo_title || tag.name}
        description={tag.meta_description}
        seo={{ ...(data.seo || {}), index: tag.is_indexed }}
        locale={locale}
        canonicalPath={`/tags/${slug}`}
        image={tag.og_image ? mediaUrl(tag.og_image) : undefined}
      />
      <Header />
      <main id="main" className="adf-container py-14 md:py-20">
        <div className="max-w-3xl">
          <div className="adf-small text-[var(--adf-gray-2)]">{t("tags.label", "Tag")}</div>
          <h1 className="adf-heading mt-4 text-5xl md:text-7xl">{title}</h1>
          {description && <p className="mt-5 max-w-2xl text-[var(--adf-gray-2)]">{description}</p>}
          <p className="mt-4 text-sm text-[var(--adf-gray-2)]">
            {productTotal} {t("tags.products", "məhsul")} · {articles.length} {t("tags.articles", "məqalə")}
          </p>
        </div>

        {!!products.length && (
          <section className="mt-16">
            <h2 className="font-serif text-3xl">{t("tags.products_title", "Məhsullar")}</h2>
            <div className="adf-product-grid mt-8">
              {explodeColourCards(products).map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} testId="tag-product" listName="tag" />
              ))}
            </div>
            {pageCount > 1 && (
              <nav className="mt-12 flex items-center justify-center gap-4" aria-label={t("tags.pagination", "Məhsul səhifələri")}>
                <button
                  type="button"
                  className="adf-cta adf-cta-outline disabled:cursor-not-allowed disabled:opacity-40"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  {t("common.previous", "Əvvəlki")}
                </button>
                <span className="text-sm text-[var(--adf-gray-2)]">{page} / {pageCount}</span>
                <button
                  type="button"
                  className="adf-cta adf-cta-outline disabled:cursor-not-allowed disabled:opacity-40"
                  disabled={page >= pageCount}
                  onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
                >
                  {t("common.next", "Növbəti")}
                </button>
              </nav>
            )}
          </section>
        )}

        {page === 1 && !!articles.length && (
          <section className="mt-20">
            <h2 className="font-serif text-3xl">{t("tags.journal_title", "Jurnaldan")}</h2>
            <div className="mt-8 grid gap-10 md:grid-cols-2">
              {articles.map((article) => (
                <Link key={article.id} to={`/journal/${article.slug}`} className="group block">
                  <div className="aspect-[16/9] overflow-hidden bg-stone/40">
                    {article.cover_image_url && (
                      <img src={mediaUrl(article.cover_image_url)} alt={pickLocale(article.title, locale)} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.025]" />
                    )}
                  </div>
                  <h3 className="mt-5 font-serif text-3xl group-hover:text-espresso">{pickLocale(article.title, locale)}</h3>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
